import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Reveal } from '@/components/Reveal';
import { useServices } from '@/data/useServices';
import { useProjects } from '@/data/useProjects';

// Keep the original six projects in their reference masonry positions, while
// still allowing any project marked from the admin panel to enter the Home.
//   Col 1: Construtop (short), Promart (tall)
//   Col 2: CeraVe (tall), Mapfre (landscape)
//   Col 3: Marathon (short), Robotic Minds (tall)
const PREFERRED_FEATURED_LAYOUT = [
  ['construtop-olimpiadas', 'promart-apertura-carapungo'],
  ['cerave-lanzamiento-productos', 'mapfre-cofficce'],
  ['marathon-aperturas-tiendas', 'robotic-minds-torneo'],
];

const buildFeaturedColumns = (projects) => {
  const featured = projects.filter((p) => p.featuredOnHome === true);
  const featuredBySlug = new Map(featured.map((p) => [p.slug, p]));
  const usedSlugs = new Set();

  const columns = PREFERRED_FEATURED_LAYOUT.map((column) =>
    column.reduce((items, slug) => {
      const project = featuredBySlug.get(slug);
      if (project) {
        items.push(project);
        usedSlugs.add(slug);
      }
      return items;
    }, [])
  );

  // Newly featured projects are appended to the shortest column. This keeps
  // the masonry balanced without requiring their slug to be hardcoded here.
  featured
    .filter((project) => !usedSlugs.has(project.slug))
    .forEach((project) => {
      const targetColumn = columns.reduce(
        (shortest, column, index) =>
          column.length < columns[shortest].length ? index : shortest,
        0
      );
      columns[targetColumn].push(project);
    });

  return columns;
};

// Per-project logo size variants for the overlay
const LOGO_SIZE_CLASSES = {
  sm: 'h-5 sm:h-[25px]',    // -30% from default (was 28/36 → 20/25)
  md: 'h-6 sm:h-[31px]',    // -15% from default (was 28/36 → 24/31)
  default: 'h-7 sm:h-9',    // 28 / 36
};

const ProjectCard = ({ p, idx, getServiceBySlug }) => {
  const svc = getServiceBySlug(p.category);
  const aspect = p.featuredAspect || '4/5';
  const logoSizeClass = LOGO_SIZE_CLASSES[p.featuredLogoSize] || LOGO_SIZE_CLASSES.default;
  return (
    <Reveal delay={idx * 0.05}>
      <Link
        to={`/proyectos/${p.slug}`}
        className="group block"
        data-testid={`featured-project-card-${p.slug}`}
      >
        <div
          className="relative overflow-hidden rounded-2xl bg-[#1A1A1A] transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_30px_80px_rgba(0,0,0,0.18)]"
          style={{ aspectRatio: aspect }}
        >
          {/* The project image stays in full color and does not change on hover. */}
          <img
            src={p.mainImage}
            alt={p.title}
            loading="lazy"
            className="w-full h-full object-cover"
          />

          {/* Orange hover treatment without grayscale or image transformation. */}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(237,107,36,0.16) 0%, rgba(237,107,36,0.42) 58%, rgba(237,107,36,0.72) 100%)',
            }}
          />

          {/* Bottom gradient + client logo overlay */}
          <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />
          {p.featuredLogo && (
            <img
              src={p.featuredLogo}
              alt={p.client}
              className={`absolute bottom-3 left-3 sm:bottom-4 sm:left-4 ${logoSizeClass} w-auto object-contain max-w-[55%] pointer-events-none drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]`}
              style={{ filter: 'brightness(0) invert(1)' }}
            />
          )}
        </div>
        {/* Title + category below image */}
        <div className="px-1 pt-4 sm:pt-5">
          <h3 className="text-[15px] sm:text-base font-medium text-[#1A1A1A] group-hover:text-[var(--brand-orange)] transition-colors leading-snug">
            {p.title}
          </h3>
          <p className="mt-2 text-xs sm:text-[13px] text-[#7a7a7a] tracking-wide">
            {svc?.name || ''}
          </p>
        </div>
      </Link>
    </Reveal>
  );
};

export const FeaturedProjects = () => {
  const { getServiceBySlug } = useServices();
  const { projects } = useProjects();
  const featuredColumns = useMemo(() => buildFeaturedColumns(projects), [projects]);

  return (
    <section
      className="bg-white text-[#58677A] pt-[10px] sm:pt-[13px] lg:pt-5 pb-16 sm:pb-20 lg:pb-28"
      data-testid="home-featured-projects-section"
    >
      <div className="container-link">
        {/* Top row: centered title + Ver todos button on the right */}
        <div className="relative mb-10 lg:mb-14">
          <h2
            className="text-center text-[#E95526] tracking-wide"
            style={{ fontSize: '26pt', fontFamily: 'Gotham, sans-serif', fontWeight: 900 }}
            data-testid="featured-projects-title"
          >
            PROYECTOS
          </h2>
          <div className="mt-5 lg:mt-0 lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 flex justify-center lg:justify-end">
            <Link
              to="/servicios-proyectos"
              className="inline-flex items-center gap-2 text-sm text-[#58677A] border border-black/15 rounded-full px-5 py-2.5 hover:bg-black/5 transition"
              data-testid="featured-projects-see-all-link"
            >
              Ver todos
              <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>

        {/* 3-column masonry — each column is a vertical stack of cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 items-start">
          {featuredColumns.map((col, colIdx) => (
            <div key={colIdx} className="flex flex-col gap-8 lg:gap-10">
              {col.map((p, idx) => (
                <ProjectCard
                  key={p.id || p.slug}
                  p={p}
                  idx={colIdx * 2 + idx}
                  getServiceBySlug={getServiceBySlug}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
